// src/pages/services/FaqMedicalDietFemale.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaHeartbeat, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { goReserve, goBackSmart } from '../../utils/navigation';
import { useTranslation } from 'react-i18next';

interface FaqMedicalDietFemaleProps {
  ipAllowed: boolean;
}

type QA = { q: string; a: string | string[] };
type AnyObj = Record<string, any>;

async function loadFromPublic(lang: 'ja' | 'zh'): Promise<AnyObj | null> {
  try {
    const res = await fetch(`/data/faq_${lang}.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as AnyObj;
  } catch {
    return null;
  }
}

function pick<T>(root: any, path: string, fallback: T): T {
  if (!root) return fallback;
  const segs = path.split('.');
  let cur: any = root;
  for (const s of segs) {
    if (cur && typeof cur === 'object' && s in cur) cur = cur[s];
    else return fallback;
  }
  return cur as T;
}

const FaqMedicalDietFemale: React.FC<FaqMedicalDietFemaleProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';

  // ✅ 本地兜底文案（中/日齐全）
  const LOCAL = {
    ja: {
      page: {
        title: 'メディカルダイエット（女性）FAQ',
        back: '戻る',
        cta_menu: 'メニュー（女性ダイエット）へ',
        cta_reserve: 'オンラインで予約する',
        disclaimer:
          '※ 本ページは一般的な情報の提供を目的としています。診断や治療の最終判断は必ず医師の診察に基づき行ってください。',
      },
      overview: {
        title: 'メディカルダイエット（女性）について',
        paras: [
          '当院のメディカルダイエットは、医師の診療のもとで科学的根拠にもとづいた医薬品を用い、食欲や代謝を整えて減量を目指すプログラムです。オンライン診療でライフステージや体質に合わせた継続支援を行います。',
          '主な選択肢：GLP-1受容体作動薬（経口：リベルサス®、注射：マンジャロ®）、SGLT2阻害薬（フォシーガ®）、ビグアナイド（メトホルミン）。生活習慣（睡眠・栄養・運動）と組み合わせて設計します。',
        ],
      },
      agents: {
        title: '主なお薬と作用の違い',
        glp1_oral: {
          k: 'リベルサス®（経口GLP-1RA）',
          v: [
            '食欲中枢に働きかけて空腹感を軽減、食後血糖の上昇をゆるやかに。',
            '毎日内服。段階的な増量で効果と副作用のバランスを調整。',
          ],
        },
        glp1_inj: {
          k: 'マンジャロ®（注射GLP-1/GIP作動薬）',
          v: [
            '週1回自己注射。食欲抑制にくわえ、エネルギー消費や脂肪分解の促進が期待できる。',
            'オンラインで自己注射の操作指導を実施。',
          ],
        },
        sglt2: {
          k: 'フォシーガ®（SGLT2阻害薬）',
          v: [
            '腎臓での糖再吸収を抑え、尿に糖を排泄（カロリー赤字を作る）。',
            'むくみが気になる方でも体液バランスに注意しつつ使用を検討。',
          ],
        },
        met: {
          k: 'メトホルミン（ビグアナイド）',
          v: [
            'インスリン抵抗性を改善し、食欲と脂肪蓄積を抑制。土台の代謝を整える目的でも有用。',
            'PCOS（多嚢胞性卵巣症候群）に伴う代謝異常への併用が検討されることがあります。',
          ],
        },
      },
      female_notes: {
        title: '女性特有の注意事項',
        items: [
          '妊娠・授乳：妊娠中・授乳中は原則として多くの減量薬が適応外。妊娠希望時は事前に医師へ相談し、薬剤の中止タイミングや代替策を計画します。',
          '月経・更年期：ホルモン変化で体重・食欲がぶれやすい時期。症状に合わせて用量やタイミングを調整します。',
          '甲状腺：甲状腺機能低下/亢進は体重に影響。必要に応じて採血で評価し、治療方針を最適化します。',
          'PCOS：インスリン抵抗性が背景にある場合、メトホルミン等の併用が奏功することがあります（個別評価）。',
          '泌尿器・婦人科感染：SGLT2阻害薬では尿路/性器感染症のリスクがやや上がるため、清潔ケアと水分補給を指導します。',
          '栄養：鉄欠乏（フェリチン低下）・タンパク質不足は疲労や抜け毛、減量停滞の要因に。必要に応じて補充と食事改善を。',
        ],
      },
      side_effects: {
        title: '副作用のめやす',
        items: [
          {
            k: 'GLP-1系（リベルサス®／マンジャロ®）',
            v: [
              '悪心・嘔気・胃部不快、満腹感の早まり（開始・増量初期に多い／多くは経過で軽快）',
              '稀に膵炎など。強い持続腹痛・嘔吐は使用を中止し受診を。',
            ],
          },
          {
            k: 'SGLT2阻害薬（フォシーガ®）',
            v: [
              '頻尿・口渇・体液量低下、尿路/性器感染症（こまめな水分・清潔ケア）',
              '稀にケトアシドーシス。著明な倦怠感・腹痛・呼吸が浅い等あれば受診。',
            ],
          },
          {
            k: 'メトホルミン',
            v: [
              '胃部不快・下痢・金属味（少量開始/食後内服で軽減）',
              '極めて稀に乳酸アシドーシス。強い倦怠感・呼吸促迫は至急受診。',
            ],
          },
        ],
        note: '※ 副作用には個人差があります。異常を感じた場合は自己判断で継続せず、医師にご相談ください。',
      },
      expectation: {
        title: '効果の見え方・タイムライン',
        points: [
          '1〜2週：空腹感の変化・食事量の自然な減少を自覚する方がいます（GLP-1系）。',
          '4〜8週：体重・ウエストの緩やかな低下。月経周期と体重の揺らぎを踏まえ評価。',
          '12週以降：停滞期は用量調整/併用/生活テコ入れで突破。再現性のある減量を積み上げます。',
        ],
        foot: '体重は「薬理＋生活習慣」の掛け算。睡眠・タンパク質・活動量・鉄/ビタミンの適正化で効率が上がります。',
      },
      lifestyle: {
        title: '併用すると良い生活アプローチ',
        items: [
          'タンパク質を毎食で確保（体重×1.0〜1.5g/日目安を個別調整）＋鉄・亜鉛・ビタミンDなど不足チェック',
          '食物繊維・発酵食品で腸内環境を整える／甘い飲料・アルコールの頻度を抑える',
          'レジスタンス運動で筋量維持、日常歩行と立位時間を増やす（NEAT底上げ）',
          '7時間前後の睡眠を安定化。PMSや更年期症状は主治医と相談しながらマネジメント',
        ],
      },
      qa: [
        {
          q: '妊娠を希望しています。開始しても大丈夫？',
          a: [
            '妊娠希望・妊娠中・授乳中は多くの減量薬が適応外です。スケジュールを医師と共有し、中止タイミングや代替策（生活療法中心）を計画します。',
            '避妊の必要性についても個別にご説明します。',
          ],
        },
        {
          q: 'どのお薬が女性に向いていますか？',
          a: [
            '食欲のつよさ、BMI、合併症、ライフステージ（月経・更年期・妊活）を踏まえて選択します。',
            'GLP-1系は食欲抑制に優れ、メトホルミンはインスリン抵抗性の改善に有用、SGLT2は排糖でエネルギー赤字を作ります。',
          ],
        },
        {
          q: 'PCOSでも使えますか？',
          a: [
            'PCOSではインスリン抵抗性が関与することが多く、メトホルミンやGLP-1系の組み合わせが検討されます。',
            '排卵・月経への影響や妊娠希望の有無を踏まえて設計します。',
          ],
        },
        {
          q: '尿路や性器の感染が心配です（SGLT2）。',
          a: [
            'SGLT2阻害薬ではリスクがやや上がるため、こまめな水分・排尿・清潔ケアを心がけます。',
            '再発する場合は薬剤の見直しや併用策を検討します。',
          ],
        },
        {
          q: '停滞期の乗り越え方は？',
          a: [
            'タンパク質・鉄（フェリチン）・ビタミンD等の不足を点検し、活動量と睡眠をテコ入れ。',
            '用量増量・併用、食事リズムの調整などを医師と相談します。',
          ],
        },
        {
          q: 'リバウンドを避けるには？',
          a: [
            '薬は行動変容をブーストする道具。食行動・運動・睡眠が固定化されるほど長期維持が容易です。',
            '中止は段階的に。体重と行動指標を見ながら計画します。',
          ],
        },
      ] as QA[],
    },

    zh: {
      page: {
        title: '医疗减重（女性）FAQ',
        back: '返回',
        cta_menu: '返回女性减重菜单',
        cta_reserve: '在线预约',
        disclaimer:
          '※ 本页面为一般性信息，不构成具体诊断或医疗建议。最终诊疗请以接诊医生意见为准。',
      },
      overview: {
        title: '关于医疗减重（女性）',
        paras: [
          '本院的医疗减重以循证用药为核心，在医生指导下通过调节食欲与代谢来达成减重目标。我们提供在线问诊，并根据不同生命阶段与体质进行持续随访与支持。',
          '主要用药选择：GLP-1 受体激动剂（口服：口服司美格鲁肽 (Rybelsus)；注射：替尔泊肽注射液 (Mounjaro)）、SGLT2 抑制剂 (达格列净片 (Forxiga))、双胍类 (二甲双胍 (Metformin))。会结合睡眠、营养与运动等生活方式共同设计方案。',
        ],
      },
      agents: {
        title: '主要药物与作用差异',
        glp1_oral: {
          k: '口服司美格鲁肽 (Rybelsus)',
          v: [
            '作用于食欲中枢，减轻饥饿感，平缓餐后血糖上升。',
            '每日口服；按疗效与耐受性分阶段加量以平衡效果与不良反应。',
          ],
        },
        glp1_inj: {
          k: '替尔泊肽注射液 (Mounjaro)',
          v: [
            '每周一次自我注射；除抑制食欲外，还可能促进能量消耗与脂肪分解。',
            '提供在线自我注射操作指导。',
          ],
        },
        sglt2: {
          k: '达格列净片 (Forxiga)',
          v: [
            '抑制肾脏对葡萄糖的重吸收，增加尿糖排泄（制造“热量赤字”）。',
            '若有水肿困扰者，需结合体液平衡个别评估与使用。',
          ],
        },
        met: {
          k: '二甲双胍 (Metformin)',
          v: [
            '改善胰岛素抵抗，抑制食欲与脂肪堆积；有助于“打好代谢底子”。',
            '在 PCOS（多囊卵巢综合征）相关代谢异常时，常被考虑联合使用（个别评估）。',
          ],
        },
      },
      female_notes: {
        title: '女性特有的注意事项',
        items: [
          '妊娠/哺乳：妊娠期与哺乳期多种减重药均不适用。若有备孕计划，请提前与医生沟通停药时点与替代方案。',
          '月经/更年期：激素波动期体重与食欲易起伏；需按症状个别调整剂量与用药时点。',
          '甲状腺：甲状腺功能异常会影响体重；必要时抽血评估并优化治疗方案。',
          'PCOS：若以胰岛素抵抗为主，联合二甲双胍 (Metformin) 等往往更合适（个别评估）。',
          '泌尿/妇科感染：SGLT2 抑制剂可略增泌尿/生殖道感染风险，需强调清洁护理与补水。',
          '营养：缺铁（铁蛋白低）与蛋白质不足可致疲劳、掉发与停滞期；需视情况补充并优化饮食。',
        ],
      },
      side_effects: {
        title: '常见不良反应参考',
        items: [
          {
            k: 'GLP-1 系 (口服司美格鲁肽 (Rybelsus) / 替尔泊肽注射液 (Mounjaro))',
            v: [
              '恶心、胃部不适、饱腹感提前（多见于开始/加量初期，通常随时间缓解）',
              '极少数出现胰腺炎等；若持续剧烈腹痛/反复呕吐应立即停药并就医。',
            ],
          },
          {
            k: 'SGLT2 抑制剂 (达格列净片 (Forxiga))',
            v: [
              '多尿、口渴、体液量下降，泌尿/生殖道感染风险略增（强调补水与清洁护理）',
              '少见酮症酸中毒；若极度乏力、腹痛、呼吸浅快等应尽快就医。',
            ],
          },
          {
            k: '二甲双胍',
            v: [
              '胃部不适、腹泻、口中金属味（小剂量起步/餐后用药可缓解）',
              '极罕见乳酸性酸中毒；若明显乏力、呼吸急促，需立即就医。',
            ],
          },
        ],
        note: '※ 不良反应存在个体差异；如有异常请勿自行持续服用，务必咨询医生。',
      },
      expectation: {
        title: '效果显现与时间线',
        points: [
          '1–2 周：部分人会感到饥饿感下降、进食量自然减少（GLP-1 类）。',
          '4–8 周：体重/腰围缓慢下降；结合月经周期对体重的自然波动来评估。',
          '12 周后：遇到平台期可通过调剂量/联用/生活方式微调来突破，积累可复制的减重成果。',
        ],
        foot: '体重变化 = 药理 × 生活方式。优化睡眠、蛋白质、活动量与铁/维生素水平能显著提升效率。',
      },
      lifestyle: {
        title: '与治疗并行的生活方式建议',
        items: [
          '每餐确保蛋白质（按体重×1.0–1.5 g/日个别调整）＋检查铁、锌、维生素 D 等缺乏',
          '用膳食纤维与发酵食品改善肠道环境；减少含糖饮料/酒精频次',
          '抗阻训练维持肌量，增加日常步行与站立时间（提升 NEAT）',
          '稳定约 7 小时睡眠；PMS/更年期症状与主治医生共同管理',
        ],
      },
      qa: [
        {
          q: '我在备孕/计划怀孕，现在可以开始吗？',
          a: [
            '备孕、妊娠与哺乳期大多减重药并不适用。请与医生沟通时间表，制定停药时点与以生活方式为主的替代方案。',
            '如需避孕也会为你做个别化说明。',
          ],
        },
        {
          q: '女性更适合哪类药物？',
          a: [
            '会综合饥饿感强度、BMI、并发症、月经/更年期/备孕等因素选择。',
            'GLP-1 类擅长抑制食欲；二甲双胍 (Metformin) 有助改善胰岛素抵抗；SGLT2 通过“排糖”制造热量赤字。',
          ],
        },
        {
          q: '合并 PCOS 可以用吗？',
          a: [
            'PCOS 多与胰岛素抵抗有关，常考虑二甲双胍 (Metformin) 或与 GLP-1 类联用。',
            '会结合排卵/月经影响与是否备孕来决定方案。',
          ],
        },
        {
          q: 'SGLT2 会增加泌尿/生殖道感染吗？',
          a: [
            '风险略有上升，因此要注意多喝水、及时排尿与清洁护理。',
            '若反复发作，可评估是否调整药物或采取其他联合方案。',
          ],
        },
        {
          q: '平台期怎么破？',
          a: [
            '先排查蛋白质、铁（铁蛋白）、维生素 D 等不足，提升活动量和睡眠质量。',
            '再与医生讨论加量/联用或调整进食节律等策略。',
          ],
        },
        {
          q: '如何避免反弹？',
          a: [
            '药物是促进行为改变的工具。饮食/运动/睡眠越“固化”，长期维持越容易。',
            '停药建议循序渐进，并结合体重与行为指标制定计划。',
          ],
        },
      ] as QA[],
    },
  } as const;

  const [root, setRoot] = useState<AnyObj | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const remote = await loadFromPublic(lang);
      if (!mounted) return;
      setRoot(remote);
    })();
    return () => {
      mounted = false;
    };
  }, [lang]);

  // JSON -> セクション取得（なければ言語別ローカルで補完）
  const J = pick<any>(root, 'faq.medical_diet_female', null);

  const page = pick(J, 'page', LOCAL[lang].page);
  const OVERVIEW_DATA = pick(J, 'overview', LOCAL[lang].overview);
  const AGENTS_DATA = pick(J, 'agents', LOCAL[lang].agents);
  const FEMALE_NOTES_DATA = pick(J, 'female_notes', LOCAL[lang].female_notes);
  const SIDE_EFFECTS_DATA = pick(J, 'side_effects', LOCAL[lang].side_effects);
  const EXPECTATION_DATA = pick(J, 'expectation', LOCAL[lang].expectation);
  const LIFESTYLE_DATA = pick(J, 'lifestyle', LOCAL[lang].lifestyle);
  const QA_LIST: QA[] = pick(J, 'qa', LOCAL[lang].qa);

  const [open, setOpen] = useState<number | null>(0);
  const toggle = (idx: number) => setOpen((cur) => (cur === idx ? null : idx));

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: QA_LIST.slice(0, 25).map((qa) => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: { '@type': 'Answer', text: Array.isArray(qa.a) ? qa.a.join('\n') : qa.a },
      })),
    }),
    [QA_LIST]
  );

  return (
    <div className="min-h-screen pt-28 pb-20 bg-rose-50/70 font-sans" translate="no">
      <Helmet>
        <title>{page.title} - Melodis Clinic</title>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-9">
          {/* Back */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => goBackSmart(navigate, '/faq')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" />
              {page.back}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaHeartbeat className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="text-rose-400 mr-2">│</span>
              {page.title}
            </h1>
          </div>

          {/* Overview */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50 p-6 md:p-7 mb-8">
            <h2 className="text-2xl font-semibold text-rose-700 mb-3">{OVERVIEW_DATA.title}</h2>
            <div className="space-y-3 text-slate-800 leading-relaxed">
              {(OVERVIEW_DATA.paras || []).map((p: string, i: number) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* Agents */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{AGENTS_DATA.title}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[AGENTS_DATA.glp1_oral, AGENTS_DATA.glp1_inj, AGENTS_DATA.sglt2, AGENTS_DATA.met]
                .filter(Boolean)
                .map((card: any, idx: number) => (
                  <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                      {(card.v || []).map((line: string, i: number) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </section>

          {/* Female-specific notes */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{FEMALE_NOTES_DATA.title}</h3>
            <ul className="list-disc pl-5 space-y-1 text-slate-800">
              {(FEMALE_NOTES_DATA.items || []).map((x: string, i: number) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </section>

          {/* Side effects */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{SIDE_EFFECTS_DATA.title}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {(SIDE_EFFECTS_DATA.items || []).map((card: any, idx: number) => (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(card.v || []).map((line: string, i: number) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {SIDE_EFFECTS_DATA.note && (
              <p className="text-slate-600 text-xs mt-3">{SIDE_EFFECTS_DATA.note}</p>
            )}
          </section>

          {/* Expectation & Lifestyle */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '疗效呈现与时间轴' : '経過の目安とコツ'}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{EXPECTATION_DATA.title}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(EXPECTATION_DATA.points || []).map((x: string, i: number) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
                {EXPECTATION_DATA.foot && (
                  <p className="text-slate-600 text-xs mt-2">{EXPECTATION_DATA.foot}</p>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{LIFESTYLE_DATA.title}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(LIFESTYLE_DATA.items || []).map((x: string, i: number) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Q&A */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '常见问题' : 'よくある質問'}
            </h3>
            <div className="space-y-3">
              {QA_LIST.map((qa, idx) => {
                const isOpen = open === idx;
                return (
                  <div key={idx} className="border border-slate-200 rounded-xl bg-slate-50">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(idx)}
                      className="w-full flex items-start justify-between gap-4 px-4 py-3 text-left"
                    >
                      <span className="font-medium text-slate-900">{qa.q}</span>
                      {isOpen ? (
                        <FaChevronUp className="mt-1 shrink-0 text-slate-400" />
                      ) : (
                        <FaChevronDown className="mt-1 shrink-0 text-slate-400" />
                      )}
                    </button>
                    <div className={`px-4 pb-4 ${isOpen ? '' : 'hidden'}`}>
                      {Array.isArray(qa.a) ? (
                        <ul className="list-disc pl-5 space-y-1 text-slate-800">
                          {(qa.a as string[]).map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-800">{qa.a as string}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-slate-500 text-xs mt-4">{page.disclaimer}</p>
          </section>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/obesity-female')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              {page.cta_menu}
            </button>
            <button
              onClick={() => goReserve(navigate, ipAllowed)}
              className="inline-block bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white px-8 py-3 rounded-full font-semibold shadow-sm transition"
            >
              {page.cta_reserve}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqMedicalDietFemale;
